import i18n from "../i18n";
import {setLogout as clearLogin} from "../util/loginStatus";

const apiErrorCodes = [
    "INVALID_REQUEST",
    "NOT_FOUND",
    "CONFLICT",
    "USER_NAME_CONFLICT",
    "LEGAL_ENTITY_NAME_CONFLICT",
    "CHAT_DOCUMENTS_UNAVAILABLE",
    "FILE_UPLOAD_FAILED",
    "TECHNICAL_ERROR",
    "UNAUTHORIZED",
    "WRONG_OLD_PASSWORD",
    "FORBIDDEN",
    "UNPROCESSABLE_CONTENT",
] as const;

export type ApiErrorCode = (typeof apiErrorCodes)[number];

type ApiErrorResponse = {
    code?: string;
    message?: string;
};

const apiErrorCodeSet = new Set<ApiErrorCode>(apiErrorCodes);

export class ApiError extends Error {
    readonly status: number;
    readonly code: ApiErrorCode;

    constructor(status: number, code: ApiErrorCode) {
        super(translateApiErrorCode(code));
        this.name = "ApiError";
        this.status = status;
        this.code = code;
    }
}

function handleGlobalApiError(error: ApiError): void {
    if (typeof window === "undefined") {
        return;
    }

    if (error.code === "UNAUTHORIZED") {
        clearLogin();
        if (window.location.pathname !== "/login") {
            window.location.replace("/login");
        }
        return;
    }

    if (error.code === "FORBIDDEN" && window.location.pathname !== "/access-denied") {
        window.location.replace("/access-denied");
    }
}

export function getBaseURL(): string {
    let baseURL: string | undefined = process.env.REACT_APP_BACKEND_SERVER_URL;

    if (!baseURL) {
        baseURL = window.location.origin;
    }

    return baseURL;
}

function getRequiredBaseURL(): string {
    const baseURL = getBaseURL();

    if (!baseURL) {
        const message: string = "REACT_APP_BACKEND_SERVER_URL is not set";
        console.error(message);
        throw new Error(message);
    }

    return baseURL;
}

export function getCsrfToken(): string | null {
    const cookie = document.cookie.split("; ").find((row) => row.startsWith("XSRF-TOKEN="));
    return cookie ? cookie.split("=")[1] : null;
}

export function getCsrfHeaders(): HeadersInit {
    const token = getCsrfToken();
    return token ? {"X-XSRF-TOKEN": token} : {};
}

export function translateApiErrorCode(code: ApiErrorCode): string {
    return i18n.t(`errors.${code}`);
}

function getFallbackApiErrorCode(status: number): ApiErrorCode {
    switch (status) {
        case 400:
            return "INVALID_REQUEST";
        case 401:
            return "UNAUTHORIZED";
        case 403:
            return "FORBIDDEN";
        case 404:
            return "NOT_FOUND";
        case 409:
            return "CONFLICT";
        case 422:
            return "UNPROCESSABLE_CONTENT";
        default:
            return "TECHNICAL_ERROR";
    }
}

function isApiErrorCode(code: string | undefined): code is ApiErrorCode {
    return code !== undefined && apiErrorCodeSet.has(code as ApiErrorCode);
}

export async function toApiError(response: Response): Promise<ApiError> {
    const fallbackCode = getFallbackApiErrorCode(response.status);
    const contentType = response.headers.get("content-type");

    if (!contentType?.includes("application/json")) {
        const error = new ApiError(response.status, fallbackCode);
        handleGlobalApiError(error);
        return error;
    }

    try {
        const body = (await response.json()) as ApiErrorResponse;
        const code = isApiErrorCode(body.code) ? body.code : fallbackCode;
        const error = new ApiError(response.status, code);
        handleGlobalApiError(error);
        return error;
    } catch {
        const error = new ApiError(response.status, fallbackCode);
        handleGlobalApiError(error);
        return error;
    }
}

export async function throwApiError(response: Response): Promise<never> {
    throw await toApiError(response);
}

export async function ensureOk(response: Response): Promise<Response> {
    if (!response.ok) {
        await throwApiError(response);
    }

    return response;
}

export async function request(path: string, init: RequestInit = {}): Promise<Response> {
    const baseURL = getRequiredBaseURL();
    const url = new URL(`${baseURL}${path}`);
    const method = (init.method ?? "GET").toString().toUpperCase();
    const csrfHeaders = method === "GET" ? {} : getCsrfHeaders();
    const isFormDataBody = init.body instanceof FormData;

    const headers: HeadersInit = isFormDataBody
        ? {
            ...csrfHeaders,
            ...(init.headers ?? {}),
        }
        : {
            "Content-Type": "application/json",
            ...csrfHeaders,
            ...(init.headers ?? {}),
        };

    return fetch(url, {
        ...init,
        method,
        headers,
        mode: "cors",
        credentials: "include",
    });
}

export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await request(path, init);
    await ensureOk(response);
    return (await response.json()) as T;
}
